import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { CopyButton } from '../../ui/copy-button';
import {
  Clock,
  Hash,
  ArrowRightLeft,
  Coins,
  Zap,
  ListChecks,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { BitcoinTransaction } from '../../Address/Bitcoin/TxHistoryTable/types';
import { satsToBtc } from '../../../utils/conversion';
import { numberWithCommas } from '../../../utils/number';
import { useBtcPrice } from '../../../hooks/usePricing';

interface TransactionSummaryProps {
  transaction: BitcoinTransaction;
}

// Helper function to calculate age
const calculateAge = (timestamp: number): string => {
  const transactionTime = timestamp * 1000; // Convert to milliseconds
  const now = new Date().getTime();
  const difference = now - transactionTime;

  const seconds = Math.floor(difference / 1000) % 60;
  const minutes = Math.floor(difference / (1000 * 60)) % 60;
  const hours = Math.floor(difference / (1000 * 60 * 60)) % 24;
  const days = Math.floor(difference / (1000 * 60 * 60 * 24)) % 30;
  const months = Math.floor(difference / (1000 * 60 * 60 * 24 * 30)) % 12;
  const years = Math.floor(difference / (1000 * 60 * 60 * 24 * 365));

  const parts = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}m`);
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);

  return parts.length > 0 ? parts.join(' ') : 'Just now';
};

// Helper function to format hash for display
const formatHash = (fullHash: string) => {
  if (fullHash.length > 20) {
    return `${fullHash.substring(0, 10)}...${fullHash.substring(fullHash.length - 10)}`;
  }
  return fullHash;
};

// Helper function to format BTC amounts
const formatBtcAmount = (
  amountInSats: number,
  decimals: number = 8,
): string => {
  const btcAmount = satsToBtc(amountInSats);
  return btcAmount.toFixed(decimals);
};

// Helper function to check if transaction is coinbase
const isCoinbaseTransaction = (transaction: BitcoinTransaction): boolean => {
  // A coinbase transaction typically has no inputs (or special inputs)
  // and is the first transaction in a block
  return (
    transaction.cpin.length === 0 ||
    (transaction.cpin.length === 1 && transaction.cpin[0].addr === '')
  );
};

export const TransactionSummary = ({
  transaction,
}: TransactionSummaryProps) => {
  const { btcPrice } = useBtcPrice();
  const [age, setAge] = useState<string>('');

  useEffect(() => {
    setAge(calculateAge(transaction.timestamp));
    // Update age every second
    const intervalId = setInterval(
      () => setAge(calculateAge(transaction.timestamp)),
      1000,
    );
    return () => clearInterval(intervalId);
  }, [transaction.timestamp]);

  // Calculate values
  const inputBtc = formatBtcAmount(transaction.inamt);
  const outputBtc = formatBtcAmount(transaction.outamt);
  const feeBtc = formatBtcAmount(transaction.txfee);

  const inputUsd = numberWithCommas(satsToBtc(transaction.inamt) * btcPrice, 2);
  const outputUsd = numberWithCommas(
    satsToBtc(transaction.outamt) * btcPrice,
    2,
  );
  const feeUsd = numberWithCommas(satsToBtc(transaction.txfee) * btcPrice, 2);

  const isCoinbase = isCoinbaseTransaction(transaction);

  return (
    <Card className="mx-auto mb-6 w-full max-w-[1500px] rounded-lg border border-muted-50 bg-background shadow-[0_0_10px_rgba(0,0,0,0.1)] dark:border-muted-200">
      <CardHeader>
        <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
          <CardTitle className="flex items-center gap-2 text-xl md:text-2xl">
            <ArrowRightLeft className="h-6 w-6 text-orange-500" />
            Transaction Summary
          </CardTitle>
          <Badge variant="outline" className="text-sm">
            Block: {transaction.block.toLocaleString()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4 px-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          {/* Column 1: Transaction Identity */}
          <div className="space-y-4 rounded-lg bg-card p-4 shadow-sm">
            <div className="space-y-2">
              <h4 className="flex items-center gap-1 font-semibold text-muted-foreground">
                <Hash className="h-4 w-4" /> Hash
              </h4>
              <div className="flex items-center gap-2">
                <code className="break-all rounded bg-muted px-2 py-1 text-xs sm:text-sm">
                  {formatHash(transaction.id)}
                </code>
                <CopyButton
                  text={transaction.id}
                  className="h-7 w-7"
                  iconSize={14}
                />
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="flex items-center gap-1 font-semibold text-muted-foreground">
                <Clock className="h-4 w-4" /> Timestamp
              </h4>
              <p
                className="text-sm"
                title={new Date(transaction.timestamp * 1000).toLocaleString()}
              >
                {new Date(transaction.timestamp * 1000).toLocaleDateString()}{' '}
                {new Date(transaction.timestamp * 1000).toLocaleTimeString()}
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="flex items-center gap-1 font-semibold text-muted-foreground">
                <Clock className="h-4 w-4" /> Age
              </h4>
              <p className="text-sm">{age}</p>
            </div>
          </div>

          {/* Column 2: Transaction Values */}
          <div className="space-y-4 rounded-lg bg-card p-4 shadow-sm">
            <div className="space-y-2">
              <h4 className="flex items-center gap-1 font-semibold text-muted-foreground">
                <Coins className="h-4 w-4" /> Output Value
              </h4>
              <p className="text-sm">
                {outputBtc} BTC{' '}
                <span className="text-xs text-muted-foreground">
                  (${outputUsd})
                </span>
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="flex items-center gap-1 font-semibold text-muted-foreground">
                <Coins className="h-4 w-4" /> Input Value
              </h4>
              <p className="text-sm">
                {inputBtc} BTC{' '}
                <span className="text-xs text-muted-foreground">
                  (${inputUsd})
                </span>
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="flex items-center gap-1 font-semibold text-muted-foreground">
                <ListChecks className="h-4 w-4" /> Inputs / Outputs
              </h4>
              <p className="text-sm">
                {transaction.input_cnt} Inputs / {transaction.output_cnt}{' '}
                Outputs
              </p>
            </div>
          </div>

          {/* Column 3: Transaction Details */}
          <div className="space-y-4 rounded-lg bg-card p-4 shadow-sm">
            <div className="space-y-2">
              <h4 className="flex items-center gap-1 font-semibold text-muted-foreground">
                <Zap className="h-4 w-4" /> Fee
              </h4>
              <p className="text-sm">
                {feeBtc} BTC{' '}
                <span className="text-xs text-muted-foreground">
                  (${feeUsd})
                </span>
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="flex items-center gap-1 font-semibold text-muted-foreground">
                Coinbase Transaction
              </h4>
              {isCoinbase ? (
                <Badge
                  variant="default"
                  className="bg-green-100 text-green-700 hover:bg-green-200"
                >
                  <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Yes
                </Badge>
              ) : (
                <Badge
                  variant="outline"
                  className="border-red-200 text-red-600"
                >
                  <XCircle className="mr-1 h-3.5 w-3.5" /> No
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
